import * as React from "react";
import {
  ChevronDown,
  ChevronRight,
  Image as ImageIcon,
  Video,
  MousePointerClick,
  Search,
  Plus,
  Trash2,
  Package,
  RefreshCw,
} from "lucide-react";
import { useToastApi } from "../utils/toastApi";
import { DraggableKanbanBoard } from "./kanban/DraggableKanbanBoard";

type TaleLite = {
  id: string;
  displayTitle: string;
  verse: string | null;
  createdAt: number;
  status: keyof KanbanColumns;
};

type KanbanColumns = {
  draft: TaleLite[];
  images: TaleLite[];
  packaged: TaleLite[];
  bound: TaleLite[];
  scheduled: TaleLite[];
  published: TaleLite[];
  failed: TaleLite[];
};

const STATUS_META: Record<keyof KanbanColumns, { label: string; hint: string }> = {
  draft: { label: "Draft", hint: "New tales" },
  images: { label: "Images", hint: "Generating art" },
  packaged: { label: "Packaged", hint: "Storyboard/audio ready" },
  bound: { label: "Bound", hint: "Linked to YouTube" },
  scheduled: { label: "Scheduled", hint: "Publish date set" },
  published: { label: "Published", hint: "Live" },
  failed: { label: "Failed", hint: "Needs attention" },
};

function timeAgo(ts: number) {
  const d = Date.now() / 1000 - ts;
  if (d < 60) return `${Math.floor(d)}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, { credentials: "include", ...init });
  if (!r.ok) throw new Error(await r.text());
  return r.json() as Promise<T>;
}

export default function KanbanBoard({ 
  data: externalData, 
  onRefresh 
}: { 
  data?: KanbanColumns | null; 
  onRefresh?: () => void; 
} = {}) {
  const toastApi = useToastApi();
  const [data, setData] = React.useState<KanbanColumns | null>(externalData || null);
  const [collapsed, setCollapsed] = React.useState<Record<keyof KanbanColumns, boolean>>({
    draft: false, images: false, packaged: false, bound: false, scheduled: false, published: false, failed: false,
  });
  const [q, setQ] = React.useState("");
  const [filter, setFilter] = React.useState<keyof KanbanColumns | "all">("all");
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [detail, setDetail] = React.useState<TaleLite | null>(null);
  const [loading, setLoading] = React.useState(false);

  // Update internal data when external data changes
  React.useEffect(() => {
    if (externalData) {
      setData(externalData);
    }
  }, [externalData]);

  const refresh = React.useCallback(async () => {
    if (onRefresh) {
      onRefresh();
      return;
    }
    
    setLoading(true);
    try {
      const rows = await api<KanbanColumns>("/api/tales/kanban");
      setData(rows);
    } catch (e: any) {
      toastApi.fail(e?.message || "Error", "Load failed");
    } finally {
      setLoading(false);
    }
  }, [toastApi, onRefresh]);

  React.useEffect(() => { refresh(); }, [refresh]);

  React.useEffect(() => {
    const onK = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        const el = document.getElementById("kanban-search") as HTMLInputElement | null;
        el?.focus();
      }
    };
    window.addEventListener("keydown", onK);
    return () => window.removeEventListener("keydown", onK);
  }, []);

  const visibleStatuses = (Object.keys(STATUS_META) as (keyof KanbanColumns)[])
    .filter((s) => filter === "all" || s === filter);

  const handleCardToggle = (id: string, item: TaleLite, multi = false) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (!multi) next.clear();
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setDetail(item);
  };

  const bulkQueueImages = async () => {
    if (!selected.size) return;
    const ids = Array.from(selected);
    try {
      await api("/api/tales/bulk", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ action: "queue-images", ids }),
      });
      toastApi.success(`Queued image jobs for ${ids.length} tale(s)`);
      setSelected(new Set());
      refresh();
    } catch (e: any) {
      toastApi.fail(e?.message || "Error", "Queue failed");
    }
  };

  const bulkPackage = async () => {
    if (!selected.size) return;
    const ids = Array.from(selected);
    try {
      await api("/api/tales/bulk", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ action: "package-video", ids }),
      });
      toastApi.success(`Packaging started for ${ids.length} tale(s)`);
      setSelected(new Set());
      refresh();
    } catch (e: any) {
      toastApi.fail(e?.message || "Error", "Package failed");
    }
  };

  const bulkDelete = async () => {
    if (!selected.size) return;
    const ids = Array.from(selected);
    try {
      await api("/api/tales/bulk", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ action: "delete", ids }),
      });
      toastApi.success(`Deleted ${ids.length} tale(s)`);
      setSelected(new Set());
      refresh();
    } catch (e: any) {
      toastApi.fail(e?.message || "Error", "Delete failed");
    }
  };

  const createNew = async () => {
    try {
      const res = await api<{ ok: boolean; taleId: string }>("/api/tales/create", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ mode: "random" }),
      });
      toastApi.success(`Tale created: ${res.taleId}`);
      refresh();
    } catch (e: any) {
      toastApi.fail(e?.message || "Error", "Create failed");
    }
  };

  const filtered = React.useMemo(() => {
    if (!data) return null;
    const out: KanbanColumns = {
      draft: [], images: [], packaged: [], bound: [], scheduled: [], published: [], failed: [],
    };
    for (const key of Object.keys(out) as (keyof KanbanColumns)[]) {
      out[key] = (data[key] || []).filter((t) => {
        const text = `${t.displayTitle} ${t.verse || ""}`.toLowerCase();
        return text.includes(q.toLowerCase());
      });
    }
    return out;
  }, [data, q]);

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      <div className="sticky top-0 z-10 bg-white/80 dark:bg-slate-900/80 backdrop-blur border-b">
        <div className="flex items-center gap-2 p-3">
          <div className="relative w-[360px] max-w-full">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-60" />
            <input
              id="kanban-search"
              placeholder="Search tales by title or verse… (⌘/Ctrl + K)"
              className="w-full pl-9 pr-3 py-2 border rounded-lg bg-white dark:bg-slate-800 dark:border-slate-600"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2 ml-2">
            <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label="All" />
            {(Object.keys(STATUS_META) as (keyof KanbanColumns)[]).map((k) => (
              <FilterChip key={k} active={filter === k} onClick={() => setFilter(k)} label={STATUS_META[k].label} />
            ))}
          </div>

          <div className="ml-auto flex items-center gap-2">
            {selected.size > 0 && (
              <>
                <button className="btn btn-sm" onClick={bulkQueueImages}>
                  <ImageIcon className="mr-2 h-4 w-4" /> Queue images
                </button>
                <button className="btn btn-sm" onClick={bulkPackage}>
                  <Package className="mr-2 h-4 w-4" /> Package
                </button>
                <button className="btn btn-sm btn-danger" onClick={bulkDelete}>
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </button>
                <div className="mx-1 h-6 w-px bg-gray-300 dark:bg-gray-600" />
              </>
            )}
            <button className="btn btn-primary btn-sm" onClick={createNew}>
              <Plus className="mr-2 h-4 w-4" /> Create New Tale
            </button>
            <button className="btn btn-sm" onClick={refresh} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Draggable Columns */}
      {!filtered ? (
        <div className="p-4">
          <SkeletonColumns />
        </div>
      ) : (
        <DraggableKanbanBoard initial={filtered} />
      )}

      {/* Details Drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setDetail(null)} />
          <div className="ml-auto w-[480px] bg-white dark:bg-slate-900 shadow-xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold truncate">{detail.displayTitle || "Untitled tale"}</h2>
                <button onClick={() => setDetail(null)} className="text-gray-500 hover:text-gray-700">
                  ×
                </button>
              </div>
              <div className="space-y-4">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <div>Status: <span className="capitalize bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-xs">{detail.status}</span></div>
                  <div>Verse: {detail.verse || "—"}</div>
                  <div>Created: {timeAgo(detail.createdAt)}</div>
                </div>
                <div className="flex gap-2">
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => window.location.assign(`/tales/${detail.id}`)}
                  >
                    <MousePointerClick className="mr-2 h-4 w-4" /> Open editor
                  </button>
                  <button
                    className="btn btn-sm"
                    onClick={async () => {
                      try {
                        await api("/api/tales/images", {
                          method: "POST",
                          headers: { "content-type": "application/json" },
                          body: JSON.stringify({ taleId: detail.id, count: 12 }),
                        });
                        toastApi.success("Queued image generation");
                        refresh();
                      } catch (e: any) {
                        toastApi.fail(e?.message || "Error", "Failed");
                      }
                    }}
                  >
                    <ImageIcon className="mr-2 h-4 w-4" /> Queue images
                  </button>
                  <button
                    className="btn btn-sm"
                    onClick={() => window.location.assign(`/tales/${detail.id}?tab=storyboard`)}
                  >
                    <Video className="mr-2 h-4 w-4" /> Package
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- UI bits ---------------- */

function FilterChip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      className={`px-3 py-1 text-sm rounded-full transition ${
        active 
          ? "bg-blue-500 text-white" 
          : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
      }`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function Column({
  title, hint, count, collapsed, onToggle, children,
}: React.PropsWithChildren<{
  title: string; hint: string; count: number; collapsed: boolean; onToggle: () => void;
}>) {
  return (
    <div className="flex h-[calc(100vh-160px)] w-[280px] min-w-[280px] flex-col rounded-xl border bg-white dark:bg-slate-800">
      <button
        onClick={onToggle}
        className="flex items-center justify-between rounded-t-xl px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700"
      >
        <div className="flex items-center gap-2">
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          <span className="font-medium">{title}</span>
          <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">{count}</span>
        </div>
        <span className="text-xs text-gray-500">{hint}</span>
      </button>
      <div className="border-t" />
      <div className={`flex-1 overflow-y-auto p-2 ${collapsed ? 'hidden' : ''}`}>
        <div className="space-y-2">{children}</div>
      </div>
    </div>
  );
}

function Card({
  item, selected, onClick, onOpen,
}: {
  item: TaleLite; selected: boolean; onClick: (e: React.MouseEvent) => void; onOpen: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={`group rounded-lg border bg-white dark:bg-slate-800 p-3 text-sm shadow-sm transition cursor-pointer hover:ring-2 hover:ring-blue-400 ${
        selected ? 'ring-2 ring-blue-500' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-medium">{item.displayTitle || "Untitled tale"}</div>
          <div className="truncate text-xs text-gray-500">{item.verse || "—"}</div>
        </div>
        <button 
          className="btn btn-xs"
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
        >
          Open
        </button>
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
        <div className="capitalize">{item.status}</div>
        <div>{timeAgo(item.createdAt)}</div>
      </div>
    </div>
  );
}

function EmptyState({ status }: { status: keyof KanbanColumns }) {
  const msg =
    status === "draft" ? "Click Create New Tale to start." :
    status === "images" ? "Queue images from a Draft tale." :
    status === "packaged" ? "Package a tale from its editor." :
    status === "bound" ? "Link to YouTube after upload." :
    status === "scheduled" ? "Schedule in YouTube Studio." :
    status === "published" ? "Nothing live yet." :
    "Check Alerts for errors.";
  return (
    <div className="mt-6 rounded-lg border border-dashed p-6 text-center text-xs text-gray-500">
      {msg}
    </div>
  );
}

function SkeletonColumns() {
  return (
    <>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-[calc(100vh-160px)] w-[280px] min-w-[280px] animate-pulse rounded-xl border bg-gray-200 dark:bg-gray-700" />
      ))}
    </>
  );
}